import { defineMessages } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";

import TourDescriptor from "@app/component/flows/types/TourDescriptor";
import CalendarViewMode from "@app/enum/CalendarViewMode";
import { applyCalendarViewSettings } from "@app/scene/activity/component/ActivityCalendarPage/store/actions";
import { getTimeframeStartForDate } from "@app/scene/activity/utils/navigation";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { createAndCondition, createSimpleCondition } from "@app/util/filters/utils";

const messages = defineMessages({
  step0: {
    id: "guides.planner.tour.step0",
    defaultMessage: "Planner View",
  },
  step0Description: {
    id: "guides.planner.tour.step0.description",
    defaultMessage:
      "Welcome to the Planner page. Here you can see all your Activities on a Calendar.",
  },
  step1: {
    id: "guides.planner.tour.step1",
    defaultMessage: "View Mode",
  },
  step1Description: {
    id: "guides.planner.tour.step1.description",
    defaultMessage:
      "Clicking here you can change calendar's view mode between Day, Week, and Month.",
  },
  step2: {
    id: "guides.planner.tour.step2",
    defaultMessage: "Adding activities",
  },
  step2Description: {
    id: "guides.planner.tour.step2.description",
    defaultMessage:
      "In order to add a new activity you can either use the Plus menu in the Navigation Bar or simply click and drag on the calendar itself.",
  },
  step3: {
    id: "guides.planner.tour.step3",
    defaultMessage: "Creating Routes",
  },
  step3Description: {
    id: "guides.planner.tour.step3.description",
    defaultMessage: "Click the ‘Go’ button to quickly create a route from Activities.",
  },
  step4: {
    id: "guides.planner.tour.step4",
    defaultMessage: "Navigation",
  },
  step4Description: {
    id: "guides.planner.tour.step4.description",
    defaultMessage:
      "You can use the navigation arrows to go to the next or previous periods: day, week, or month, depending on selected View Mode. Click Today to return to a current period.",
  },
  step5: {
    id: "guides.planner.tour.step5",
    defaultMessage: "Navigation",
  },
  step5Description: {
    id: "guides.planner.tour.step5.description",
    defaultMessage: "You can also use this mini-calendar for even quicker navigation.",
  },
  step6: {
    id: "guides.planner.tour.step6",
    defaultMessage: "List View",
  },
  step6Description: {
    id: "guides.planner.tour.step6.description",
    defaultMessage:
      "List view is a place where you can see all your activities, take notes easily, mark them done, etc.",
  },
  step7: {
    id: "guides.planner.tour.step7",
    defaultMessage: "Activity Status",
  },
  step7Description: {
    id: "guides.planner.tour.step7.description",
    defaultMessage:
      "Here you can quickly filter visible activities by their status. Please choose an Overdue status to finish this tour.",
  },
});

export const plannerTourConfiguration: TourDescriptor = {
  id: "mmc-planner-tour",
  steps: [
    {
      id: "intro",
      description: messages.step0Description,
      goToPathBefore: "/activity/calendar",
      onBefore: async ({ store }) => {
        store.dispatch(
          applyCalendarViewSettings({
            filter: createAndCondition([
              createAndCondition(
                [
                  createSimpleCondition(
                    ActivityFieldName.START_AT,
                    FilterOperator.ON,
                    getTimeframeStartForDate(CalendarViewMode.WEEK).toISOString()
                  ),
                ],
                EntityType.ACTIVITY
              ),
            ]),
            viewMode: CalendarViewMode.WEEK,
          })
        );
      },
      title: messages.step0,
    },
    {
      id: "view-mode",
      description: messages.step1Description,
      position: "bottom",
      selector: "[data-toolbar-item=viewModeButton]",
      title: messages.step1,
    },
    {
      id: "planner-info",
      description: messages.step2Description,
      title: messages.step2,
    },
    {
      id: "creating-routes",
      description: messages.step3Description,
      position: "right",
      selector: "[class*=Map_buildRoutes]",
      title: messages.step3,
    },
    {
      id: "navigating-1",
      description: messages.step4Description,
      position: "bottom",
      selector: "[class*=CalendarToolbar_left]",
      title: messages.step4,
    },
    {
      id: "navigating-2",
      description: messages.step5Description,
      position: "bottom",
      selector: "[class*=MiniCalendar_container]",
      title: messages.step5,
    },
    {
      id: "list-view",
      description: messages.step6Description,
      goToPathBefore: "/activity/list",
      selector: "[data-element-id=mode-selector] > :nth-child(2)",
      title: messages.step6,
    },
    {
      id: "status",
      description: messages.step7Description,
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      selector: "[data-toolbar-item=quickStatusFilter]",
      title: messages.step7,
      waitForEvent: {
        eventType: "internal",
        properties: { value: "overdue" },
        selector: "Clicked: Activity List -> Activity Status Filter",
      },
    },
  ],
};
